// Tutorial data for different pages
import React from 'react';

// Dashboard tutorial
export const dashboardTutorial = [
  {
    title: "Dashboard Overview",
    content: "Welcome to the Business Resilience Navigator dashboard. Here you can monitor your organization's resilience status and access key assessment information across different categories.",
    targetElement: "dashboard-container"
  },
  {
    title: "Organization Selection",
    content: "Use the Organization Selector to switch between different organizations you have access to. All assessments, metrics, and insights will update based on the selected organization.",
    targetElement: "organization-selector"
  },
  {
    title: "Assessment Selection",
    content: "Browse and select assessments from this panel. You can see completion status, scores, and when they were last updated. Click on any assessment to view its detailed maturity information.",
    targetElement: "assessment-selector"
  },
  {
    title: "Key Findings",
    content: "This panel displays AI-generated insights based on your assessment responses. It highlights strengths, weaknesses, and specific areas where your organization can improve its resilience posture.",
    targetElement: "industry-insights"
  },
  {
    title: "Maturity Score",
    content: "The circular progress indicator shows your organization's overall maturity score based on the selected assessment. Click the information icon to view detailed explanations of each maturity level.",
    targetElement: "maturity-score"
  }
];

// User Management tutorial
export const userManagementTutorial = [
  {
    title: "User Management",
    content: "This screen allows you to manage users within your organization. You can add new users, assign them to specific folders, and control their access levels based on roles.",
    targetElement: "user-management-container"
  },
  {
    title: "Adding Users",
    content: "Click the 'Add User' button to create a new user account. You'll need to provide their email, name, and assign appropriate permissions. A secure password will be automatically generated and emailed to the new user.",
    targetElement: "add-user-button"
  },
  {
    title: "User Assignments",
    content: "Users can be assigned to specific folders, giving them access to the templates and assessments within those folders. Use the folder assignment panel to control which organizational units each user can access.",
    targetElement: "user-assignments"
  },
  {
    title: "User Roles",
    content: "Each user can be assigned a role such as Admin, Manager, or User. Admins have full access to all features including user management, Managers can manage content within their assigned folders, and Users have limited access based on their assignments.",
    targetElement: "user-roles"
  },
  {
    title: "User Status",
    content: "You can activate or deactivate user accounts as needed. Deactivated users cannot log in but their account information and assignments are preserved for future reactivation.",
    targetElement: "user-status"
  }
];

// Template Library tutorial
export const templateLibraryTutorial = [
  {
    title: "Template Library",
    content: "The Template Library contains all available assessment templates. Our robust library includes templates for various industries and regions, including specialized assessments like the UK ORQuest Operational Resilience Assessment.",
    targetElement: "template-library-container"
  },
  {
    title: "Regional Tabs",
    content: "Templates are organized by region (Universal, US, UK, EU) to help you find relevant assessments for your regulatory environment. Click on a region tab to filter templates applicable to that jurisdiction.",
    targetElement: "template-categories"
  },
  {
    title: "Industry Categories",
    content: "Within each region, templates are further organized by industry type such as Financial Services, Information Security, Critical Infrastructure, and more. This helps you quickly find assessments relevant to your sector.",
    targetElement: "industry-categories"
  },
  {
    title: "Custom Templates",
    content: "You can create your own custom templates tailored to your organization's specific needs. Click 'Create Template' to start building a new assessment framework with your own categories and questions.",
    targetElement: "create-template-button"
  }
];

// Assessment tutorial
export const assessmentTutorial = [
  {
    title: "Assessments",
    content: "This page displays all your organization's assessments. You can view completed assessments, continue in-progress ones, or start new assessments based on available templates.",
    targetElement: "assessment-container"
  },
  {
    title: "Creating Assessments",
    content: "To start a new assessment, click the 'New Assessment' button and select a template. You can filter templates by region (Universal, US, UK, EU) and industry type to find the most relevant assessment for your needs.",
    targetElement: "create-assessment-button"
  },
  {
    title: "Assessment Categories",
    content: "Each assessment is organized into categories. Click on a category tab to view and answer questions for that section. The badge on each category shows your completion progress (questions answered vs. total questions).",
    targetElement: "assessment-categories"
  },
  {
    title: "Not Applicable Options",
    content: "If a category or specific question doesn't apply to your organization, you can mark it as 'Not Applicable' using the checkbox. This will exclude it from your maturity score calculation.",
    targetElement: "not-applicable-options"
  },
  {
    title: "Assessment Results",
    content: "Once completed, assessments generate detailed results with maturity scores, identified strengths, and recommended improvement actions specific to your responses.",
    targetElement: "assessment-results"
  }
];

// Reports tutorial
export const reportsTutorial = [
  {
    title: "Reports Dashboard",
    content: "The Reports section allows you to generate, view, and share comprehensive analyses of your assessment results and resilience metrics. These reports provide actionable insights for improving your organization's resilience posture.",
    targetElement: "reports-container"
  },
  {
    title: "Maturity Scorecard",
    content: "The Maturity Scorecard provides a visual representation of your assessment results across different categories. Each category is color-coded based on maturity level, making it easy to identify strengths and areas for improvement.",
    targetElement: "maturity-scorecard"
  },
  {
    title: "Report Types",
    content: "Choose from different report formats including Executive Summaries for leadership, Detailed Analysis for practitioners, and Comparative Reports to track changes over time.",
    targetElement: "report-types"
  },
  {
    title: "Custom Reports",
    content: "Create custom reports by selecting specific assessment sections, metrics, and visualization options that matter most to your stakeholders. Filter by category, maturity level, or specific questions to focus on areas of interest.",
    targetElement: "custom-report-options"
  },
  {
    title: "Exporting Reports",
    content: "Export reports as PDFs for sharing with stakeholders. The exported reports include all visualizations, maturity scores, and recommended improvement actions based on your assessment results.",
    targetElement: "report-sharing"
  }
];

// Organization Structure tutorial
export const organizationStructureTutorial = [
  {
    title: "Organization Details",
    content: "This page allows you to manage your organization's profile, structure, and settings. You can update organization information, manage folders, and configure access controls for your users.",
    targetElement: "organization-structure-container"
  },
  {
    title: "Folder Management",
    content: "Create and manage folders to organize your assessments and templates. Folders can represent departments, business units, regions, or projects within your organization. Templates and users will be assigned to these folders.",
    targetElement: "folder-management"
  },
  {
    title: "Template Assignments",
    content: "Assign templates to specific folders to make them available to users with access to those folders. This ensures users only see assessment templates relevant to their role or department.",
    targetElement: "template-folder-assignments"
  },
  {
    title: "User Assignments",
    content: "Assign users to specific folders to control access to assessments and templates. Users will only be able to see and work with content in folders they've been assigned to, maintaining proper data governance and security.",
    targetElement: "user-folder-assignments"
  },
  {
    title: "Organization Settings",
    content: "Configure organization-wide settings including branding options, default templates, and regional preferences to customize the platform for your specific needs.",
    targetElement: "organization-settings"
  }
];

// Analytics tutorial
export const analyticsTutorial = [
  {
    title: "Analytics Dashboard",
    content: "The Analytics dashboard provides visual insights into your organization's resilience posture across multiple assessments and time periods. Use these visualizations to identify trends, strengths, and areas for improvement.",
    targetElement: "analytics-container"
  },
  {
    title: "Maturity Score Breakdown",
    content: "This chart breaks down your maturity scores by category, allowing you to identify which areas of your resilience program are strongest and which need improvement. Hover over each section for detailed information.",
    targetElement: "maturity-breakdown"
  },
  {
    title: "Industry Comparison",
    content: "Compare your organization's resilience scores against industry averages. This helps identify where you stand relative to peers and highlights competitive advantages or gaps in your resilience program.",
    targetElement: "industry-comparison"
  },
  {
    title: "Trend Analysis",
    content: "Track how your resilience scores have changed over time. Positive trends indicate successful improvement initiatives, while negative trends may require attention. Use the date filters to adjust the time period displayed.",
    targetElement: "trend-analysis"
  },
  {
    title: "AI-Powered Recommendations",
    content: "Based on your assessment results, our AI system generates targeted recommendations to improve your resilience posture in the most critical areas. These actionable insights help prioritize your improvement efforts.",
    targetElement: "improvement-recommendations"
  }
];

// Data Tables tutorial
export const dataTablesTutorial = [
  {
    title: "Data Tables",
    content: "Data Tables provide access to structured information that supports your resilience planning. Here you can view, filter, and manage various datasets used throughout the system.",
    targetElement: "data-tables-container"
  },
  {
    title: "Drop Lists",
    content: "Drop lists contain standardized options used throughout the system, such as industry types (Financial Services, Information Security, etc.), regions, and assessment classifications. These ensure consistency in your data and proper categorization.",
    targetElement: "drop-lists"
  },
  {
    title: "Industry Type Management",
    content: "Industry types are used to categorize assessments. Ensure industry types are consistent between the backend and frontend to avoid categorization issues. Special cases like 'AI Act' and 'CCA' are mapped to specific industry types.",
    targetElement: "industry-types"
  },
  {
    title: "Data Filtering",
    content: "Use the search and filter options to quickly find specific information within large datasets. You can filter by multiple criteria simultaneously to narrow down results.",
    targetElement: "data-filters"
  },
  {
    title: "Data Export",
    content: "Export any table to CSV or Excel format for offline analysis or reporting. Click the export button in the table header to download the current view.",
    targetElement: "export-data-button"
  }
];

// Implementation Roadmap tutorial
export const implementationRoadmapTutorial = [
  {
    title: "Implementation Roadmap",
    content: "The Implementation Roadmap provides a visual timeline of your resilience improvement actions based on assessment results. It helps you plan, track, and prioritize implementation activities across your organization.",
    targetElement: "roadmap-container"
  },
  {
    title: "Gantt Chart View",
    content: "The Gantt chart displays your implementation activities on a timeline. Each bar represents a task with its start and end dates, making it easy to visualize your implementation schedule and identify dependencies between tasks.",
    targetElement: "gantt-chart"
  },
  {
    title: "Task Management",
    content: "Add, edit, or delete implementation tasks directly from the roadmap. You can assign owners, set priorities, establish dependencies, and update completion status to keep your implementation on track.",
    targetElement: "task-management"
  },
  {
    title: "Assessment Integration",
    content: "Tasks can be linked directly to specific assessment findings, creating a clear connection between identified gaps and improvement actions. This ensures your implementation plan addresses the most critical resilience issues.",
    targetElement: "assessment-integration"
  },
  {
    title: "Export Options",
    content: "Export your roadmap as a PDF or share it with stakeholders. The exported document includes the Gantt chart, task details, assigned owners, and due dates for comprehensive implementation planning and tracking.",
    targetElement: "roadmap-export"
  }
];

// Map tutorials to paths
export const tutorialPathMap = {
  '/dashboard': dashboardTutorial,
  '/admin/users': userManagementTutorial,
  '/admin/templates': templateLibraryTutorial,
  '/assessments': assessmentTutorial,
  '/reports': reportsTutorial,
  '/organization-details': organizationStructureTutorial,
  '/analytics': analyticsTutorial,
  '/admin/database': dataTablesTutorial,
  '/admin/droplists': dataTablesTutorial,
  '/implementation-roadmap': implementationRoadmapTutorial
};

// Get tutorial for current path
export const getTutorialForPath = (path) => {
  // Check for exact path match
  if (tutorialPathMap[path]) {
    return tutorialPathMap[path];
  }
  
  // Check for partial path matches
  for (const tutorialPath in tutorialPathMap) {
    if (path.startsWith(tutorialPath)) {
      return tutorialPathMap[tutorialPath];
    }
  }
  
  // Default to dashboard tutorial if no match found
  return dashboardTutorial;
};
